// ** React Imports
import React, { useCallback, useEffect, useState } from 'react'

// **MUI Imports
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Typography,
  useTheme
} from '@mui/material'

// **Third party
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

// **Component Imports
import ControlledTextfield from '../../../../components/controlled-textfield'
import CustomDialog from '../../../../components/dialog'

// **API Imports
import { concludeAssessment } from '../../../../axios/api/contest'

// ** Utils Imports
import {
  hexToRGBA,
  rgbaToHexWithoutOpacity,
  showErrorToast
} from '../../../../utils/utils'
import {
  scoreValidation,
  validateRequiredString
} from '../../../../utils/validations'

// **Third party Imports
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { ASSESSMENT_STAGE_STATUS } from '../../../../utils/constant'
import Icon from '../../../../components/icon'

// **YUP Schema
const schema = yup.object().shape({
  score: scoreValidation(),
  feedback: validateRequiredString('Feedback')
})

// **YUP Default Values
const defaultValues = {
  score: null,
  feedback: ''
}

const ConcludeCandidateModal = ({
  open,
  handleClose,
  title,
  rejectCandidate = false,
  candidateIds = [], // for conclude
  attemptIds = [], // for conclude and reject
  assessmentId = null, // for conclude
  refreshHandler = () => {},
  handleReject = () => {}, // for conclude and reject
  rejectLoading = false,
  statusCode = null
}) => {
  const theme = useTheme()

  // states
  const [loading, setLoading] = useState(false)

  // form hook
  const {
    control,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  // handlers
  const handleCandidateConclusion = useCallback(
    async (payload) => {
      setLoading(true)
      const response = await concludeAssessment(payload)
      setLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        toast.success(
          `${candidateIds?.length > 1 ? 'Candidates' : 'Candidate'} ${
            rejectCandidate ? 'rejected' : 'concluded'
          } successfully.`
        )
        refreshHandler(true)
        handleClose()
      }
    },
    [handleClose, refreshHandler, candidateIds?.length, rejectCandidate]
  )

  const confirmation = useCallback(async (candidates) => {
    const value = await Swal.fire({
      icon: 'info',
      text: `Are you sure you want to reject the selected ${
        candidates?.length > 1 ? 'candidates' : 'candidate'
      }?`,
      confirmButtonText: 'Confirm',
      showCancelButton: true,
      cancelButtonText: 'Cancel'
    })

    return value?.isConfirmed
  }, [])

  const onSubmit = useCallback(
    async (data) => {
      const payload = { ...data, candidateIds, assessmentId }
      if (rejectCandidate) {
        delete payload.candidateIds // we will send attemptIds for conclude & reject and candidateIds for conclude
        payload.attemptIds = attemptIds
        const isConfirmed = await confirmation(attemptIds?.length)
        if (isConfirmed) {
          handleReject(payload)
        }
        return
      }
      handleCandidateConclusion(payload)
    },
    [
      candidateIds,
      handleCandidateConclusion,
      assessmentId,
      rejectCandidate,
      handleReject,
      attemptIds,
      confirmation
    ]
  )

  // effect to pre-fill data for rejecting candidate
  useEffect(() => {
    if (open) {
      if (rejectCandidate) {
        setValue('score', 0)
        setValue('feedback', 'Candidate not selected.')
      } else {
        setValue('score', null)
        setValue('feedback', '')
      }
    } else {
      clearErrors()
    }
  }, [rejectCandidate, open, clearErrors, setValue])

  return (
    <CustomDialog
      open={open}
      outsideClikClose={true}
      handleClose={handleClose}
      title={
        title ||
        `${rejectCandidate ? 'Reject Candidate' : 'Shortlist for Offer'}`
      }
    >
      {open && (
        <Box>
          <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Box mb={4}>
              <ControlledTextfield
                fullWidth
                required
                name="score"
                numericValue
                label="Score"
                control={control}
                error={Boolean(errors.score)}
                helperText={errors.score?.message}
                placeholder="Enter a score between 0 and 10"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">/10</InputAdornment>
                  )
                }}
              />
            </Box>
            <Box mb={4}>
              <ControlledTextfield
                multiline
                fullWidth
                required
                minRows={4}
                name="feedback"
                control={control}
                label="Feedback"
                error={Boolean(errors.feedback)}
                helperText={errors.feedback?.message}
              />
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading || rejectLoading}
            >
              {loading || rejectLoading ? (
                <CircularProgress size={22} />
              ) : rejectCandidate ? (
                'Reject'
              ) : (
                'Submit'
              )}
            </Button>
          </form>
          {(statusCode === ASSESSMENT_STAGE_STATUS.NOT_STARTED ||
            statusCode === ASSESSMENT_STAGE_STATUS.NO_SHOW) && (
            <Box
              sx={{
                backgroundColor: `${rgbaToHexWithoutOpacity(
                  ...hexToRGBA(theme.palette.success.main, 0.2, true)
                )}`,
                display: 'flex',
                borderRadius: '6px',
                gap: '6px',
                padding: '8px 8px',
                width: '100%',
                marginTop: '12px',
                alignItems: 'flex-start'
              }}
            >
              <Icon
                icon="qlementine-icons:money-16"
                fontSize="18px"
                style={{ paddingTop: '2px' }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'var(--grey-800)',
                  letterSpacing: '0.2px',
                  margin: 0
                }}
              >
                This action will refund credits because the candidate did not
                join or start the current stage.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </CustomDialog>
  )
}

export default ConcludeCandidateModal
